import { useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { ALL_ASSISTANTS } from "@consts";
import { useFetchDashboardSettings } from "src/queries/hooks/settings";
import useGeneralSettings from "./general/useGeneralSettings";
import useDeploymentSettings from "./deployment/useDeploymentSettings";

const useAgentSettings = () => {
  const navigate = useNavigate();
  const { assistantId, accountId } = useParams();
  const location = useLocation();
  const rowData = location.state;
  const isAdminContext = location.pathname.includes("/admin/");
  const dashboardSettings = useFetchDashboardSettings()?.data;
  const isReadOnly = !!dashboardSettings?.disableChanges;

  const [feedbackConfig, setFeedbackConfig] = useState({
    show: false,
    message: "",
    severity: "success"
  });

  const initialRow = rowData || { id: assistantId, accountId };

  const {
    isPublishDialogOpen,
    setIsPublishDialogOpen,
    assistantSettingsProps,
    areSettingsChanged: areGeneralSettingsChanged,
    isLoading: isGeneralLoading,
    clearAssistantSettings,
    publishSettings: publishGeneralSettings,
    settingsInitialData
  } = useGeneralSettings(setFeedbackConfig, initialRow);

  const {
    areSettingsChanged: areDeploymentSettingsChanged,
    isLoading: isDeploymentLoading,
    isSubmitting: isDeploymentSubmitting,
    fetchSettings: fetchDeploymentSettings,
    clear: clearDeploymentSettings,
    submit: submitDeploymentSettings,
    initialData: deploymentInitialData,
    fieldProps: deploymentFieldProps
  } = useDeploymentSettings(setFeedbackConfig);

  const [hasFetchedDeployment, setHasFetchedDeployment] = useState(false);
  if (initialRow?.id && !hasFetchedDeployment && !isDeploymentLoading && !deploymentInitialData) {
    setHasFetchedDeployment(true);
    fetchDeploymentSettings(initialRow);
  }

  const areSettingsChanged = areGeneralSettingsChanged || areDeploymentSettingsChanged;
  const isLoading = isGeneralLoading || isDeploymentSubmitting;

  const navigateBack = () => {
    if (isAdminContext) {
      navigate(`/admin/${ALL_ASSISTANTS}`, { replace: true });
    } else {
      navigate(-1);
    }
  };

  const handlePublish = async () => {
    let allSucceeded = true;
    if (areGeneralSettingsChanged) {
      const generalOk = await publishGeneralSettings();
      if (!generalOk) allSucceeded = false;
    }
    if (areDeploymentSettingsChanged) {
      const deploymentOk = await submitDeploymentSettings();
      if (!deploymentOk) allSucceeded = false;
    }
    setIsPublishDialogOpen(false);
    if (allSucceeded) {
      navigateBack();
    }
  };

  const handleDiscard = () => {
    clearAssistantSettings();
    clearDeploymentSettings();
    navigateBack();
  };

  return {
    assistantId,
    isAdminContext,
    isPublishDialogOpen,
    setIsPublishDialogOpen,
    assistantSettingsProps,
    areSettingsChanged,
    isLoading,
    handlePublish,
    handleDiscard,
    feedbackConfig,
    setFeedbackConfig,
    settingsInitialData,
    deploymentFieldProps,
    isDeploymentSubmitting,
    isReadOnly
  };
};

export default useAgentSettings;
